'use client';

import { FC } from 'react';

import { MappedOrderHeader, OrderBaseUrls, OrderDocumentResponse } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { useTranslations } from '@ravago/shared/translations/client';

import { OrderDocuments } from '../../../shared/order-documents/order-documents';
import { OrderPageHeader } from '../../../shared/order-page-header/order-page-header';
import { DeliveryInformation } from '../../../shared/order/delivery-information';
import { InvoiceInformation } from '../../../shared/order/invoice-information';
import { OrderDetailsOrderLines } from '../../../shared/order/order-details-order-lines';
import { OrderShipments } from '../../../shared/order/order-shipments';

export type Props = {
   globals: RendererGlobals;
   order: MappedOrderHeader;
   hasDocuments: boolean;
   orderDocuments: OrderDocumentResponse | undefined;
   showVatCode: boolean;
   orderBaseUrls: OrderBaseUrls;
   isUSBusinessContext: boolean;
};

export const OrderDetails: FC<Props> = ({
   globals: {
      locale,
      acceptWebp,
      isConsumer,
      actions: { requestBol, requestCoa, requestDeliveryNote, downloadDocument }
   },
   order,
   hasDocuments,
   orderDocuments,
   showVatCode,
   orderBaseUrls,
   isUSBusinessContext
}) => {
   const translate = useTranslations();

   if (order) {
      return (
         <Box direction="column">
            <OrderPageHeader headerText={translate('my-orders.order-header')} id={order.id.toString()} status={order.status} date={undefined} />
            <OrderShipments
               isConsumer={isConsumer}
               acceptWebp={acceptWebp}
               locale={locale}
               orderDetail={order.mappedOrders}
               openedFrom="order_detail"
               isUSBusinessContext={isUSBusinessContext}
               orderBaseUrls={orderBaseUrls}
               requestCoa={requestCoa}
               requestDeliveryNote={requestDeliveryNote}
               requestBol={requestBol}
               downloadDocument={downloadDocument}
            />
            <OrderDetailsOrderLines
               isConsumer={isConsumer}
               acceptWebp={acceptWebp}
               locale={locale}
               orderDetail={order.mappedOrders}
               orderBaseUrls={orderBaseUrls}
               openedFrom="order_detail"
               requestCoa={requestCoa}
               requestBol={requestBol}
               requestDeliveryNote={requestDeliveryNote}
               downloadDocument={downloadDocument}
               isUSBusinessContext={isUSBusinessContext}
            />
            {order.invoiceInfo && (
               <>
                  <Divider />
                  <InvoiceInformation vatCode={order.invoiceInfo.vatCode} invoiceEntity={order.invoiceInfo.ole} showVatCode={showVatCode} />
               </>
            )}
            {order.deliveryInfo && (
               <>
                  <Divider />
                  <DeliveryInformation
                     destinationAddress={order.deliveryInfo.destinationAddress}
                     deliveryTermAddress={order.deliveryInfo.deliveryTermAddress}
                     deliveryInstructions={order.deliveryInfo.deliveryInstructions}
                     deliveryTerm={order.deliveryInfo.deliveryTerm}
                  />
               </>
            )}
            {hasDocuments && (
               <>
                  <Divider />
                  <OrderDocuments
                     isUSBusinessContext={isUSBusinessContext}
                     documents={orderDocuments}
                     orders={order}
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                  />
               </>
            )}
         </Box>
      );
   }

   return (
      <Box direction="column" items="center">
         <Spinner />
      </Box>
   );
};
